import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api, absUploadUrl } from "../../lib/apiClient";
import { useI18n } from "../../i18n/I18nProvider";
import { offerDetailPath } from "../../lib/offerUrl";

const STORAGE_KEY = "oj_offer_popup_dismissed_until";
const HIDE_DAYS = 7;
const HIDE_ON_ROUTES = [/^\/admin(\/.*)?$/, /^\/offers(\/.*)?$/];

export default function OfferPopup() {
  const { t, locale } = useI18n();
  const location = useLocation();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Don't show on admin or offers pages
    if (HIDE_ON_ROUTES.some((r) => r.test(location.pathname))) return;

    // Check dismissal window
    try {
      const untilStr = localStorage.getItem(STORAGE_KEY);
      if (untilStr) {
        const until = parseInt(untilStr, 10);
        if (!Number.isNaN(until) && until > Date.now()) return;
      }
    } catch {
      /* localStorage disabled */
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/offers/popup?locale=${locale}`);
        if (cancelled || !data.offer) return;
        setOffer(data.offer);
        // Delay a bit so the popup doesn't stomp initial paint
        setTimeout(() => !cancelled && setOpen(true), 1500);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, locale]);

  function dismiss() {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + HIDE_DAYS * 24 * 3600 * 1000));
    } catch {
      /* ignore */
    }
  }
  function claim() {
    dismiss();
    const target = offerDetailPath(offer, locale);
    nav(target);
  }

  if (!open || !offer) return null;
  const img = offer.popup_image_url || offer.hero_image_url || offer.list_image_url;

  return (
    <div
      data-testid="offer-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-oj-ink/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white overflow-hidden shadow-2xl"
      >
        <button
          type="button"
          data-testid="offer-popup-close"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white text-oj-ink rounded-full p-1.5 shadow"
        >
          <X className="size-4" />
        </button>
        <button
          type="button"
          data-testid="offer-popup-claim"
          onClick={claim}
          className="block w-full text-left"
        >
          {img ? (
            <img src={absUploadUrl(img)} alt={offer.title} className="w-full aspect-[4/3] object-cover" />
          ) : (
            <div className="w-full aspect-[4/3] bg-gradient-to-br from-oj-brand/30 to-oj-deep/10" />
          )}
          <div className="p-6">
            {offer.discount_label && (
              <div className="inline-flex items-center bg-oj-ink text-white text-xs font-bold px-2.5 py-1 rounded-full">
                {offer.discount_label}
              </div>
            )}
            <h3 className="mt-3 font-display text-2xl font-bold text-oj-ink">{offer.title}</h3>
            {offer.subtitle && (
              <p className="mt-1.5 text-sm text-zinc-600">{offer.subtitle}</p>
            )}
            <div className="mt-4 inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep text-sm font-semibold px-4 py-2.5 rounded-md">
              {t("offers.popup_cta")}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
